import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { METRIC_SERIES_DARK, METRIC_SERIES_LIGHT } from './metric-palette';

export interface DonutSegment {
    /** Stable identity; colour follows `slot`, never the segment's rank. */
    key: string;
    label: string;
    value: number;
    /** Categorical palette slot, or null for the neutral "rest" (e.g. free time). */
    slot: number | null;
}

const RADIUS = 40;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
/** Surface gap between segments, in viewBox units (about 2px at the default size). */
const GAP = 1;

/**
 * Part-to-whole donut for a handful (<= 6) of disjoint shares, e.g. how a room's time was spent.
 * Plain SVG with a legend that lists every value, so identity is never colour alone; hovering a
 * segment or legend row highlights it.
 */
@Component({
    selector: 'app-metric-donut',
    standalone: true,
    imports: [DecimalPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (total() > 0) {
            <div class="donut">
                <svg class="donut__svg" viewBox="0 0 100 100" role="img" [attr.aria-label]="ariaLabel()">
                    <circle class="donut__track" cx="50" cy="50" [attr.r]="radius" />
                    @for (arc of arcs(); track arc.key) {
                        <circle
                            class="donut__arc"
                            [class.donut__arc--neutral]="arc.slot === null"
                            [class.donut__arc--dim]="active() !== null && active() !== arc.key"
                            cx="50"
                            cy="50"
                            [attr.r]="radius"
                            [style.--c-light]="arc.light"
                            [style.--c-dark]="arc.dark"
                            [attr.stroke-dasharray]="arc.dash"
                            [attr.stroke-dashoffset]="arc.offset"
                            (mouseenter)="active.set(arc.key)"
                            (mouseleave)="active.set(null)"
                        >
                            <title>{{ arc.label }}: {{ arc.value | number: format() }} {{ unit() }} ({{ arc.pct | number: '1.0-0' }} %)</title>
                        </circle>
                    }
                </svg>
                @if (centerValue() !== null) {
                    <div class="donut__center" aria-hidden="true">
                        <span class="donut__center-value"
                            >{{ centerValue() | number: centerFormat() }}<span class="donut__center-unit">{{ centerUnit() }}</span></span
                        >
                        @if (centerLabel()) {
                            <span class="donut__center-label">{{ centerLabel() }}</span>
                        }
                    </div>
                }
            </div>
            <ul class="legend">
                @for (arc of arcs(); track arc.key) {
                    <li class="legend__row" [class.legend__row--dim]="active() !== null && active() !== arc.key" (mouseenter)="active.set(arc.key)" (mouseleave)="active.set(null)">
                        <span class="legend__swatch" [class.legend__swatch--neutral]="arc.slot === null" [style.--c-light]="arc.light" [style.--c-dark]="arc.dark" aria-hidden="true"></span>
                        <span class="legend__label">{{ arc.label }}</span>
                        <span class="legend__value">{{ arc.value | number: format() }} {{ unit() }}</span>
                        <span class="legend__pct">{{ arc.pct | number: '1.0-0' }} %</span>
                    </li>
                }
            </ul>
        } @else {
            <div class="donut-empty">{{ emptyText() }}</div>
        }
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-wrap: wrap;
                gap: var(--app-space-4, 1rem);
                align-items: center;
                justify-content: center;
                min-width: 0;
            }
            .donut {
                position: relative;
                width: clamp(9rem, 40%, 13rem);
                aspect-ratio: 1;
                flex: none;
            }
            .donut__svg {
                display: block;
                width: 100%;
                height: 100%;
                transform: rotate(-90deg);
            }
            .donut__track,
            .donut__arc {
                fill: none;
                stroke-width: 14;
            }
            .donut__track {
                stroke: var(--surface-border);
                opacity: 0.35;
            }
            .donut__arc {
                stroke: var(--c-light);
                transition: opacity 120ms ease;
                cursor: default;
            }
            :host-context(.app-dark) .donut__arc {
                stroke: var(--c-dark);
            }
            .donut__arc--neutral,
            :host-context(.app-dark) .donut__arc--neutral {
                stroke: var(--surface-border);
            }
            .donut__arc--dim,
            .legend__row--dim {
                opacity: 0.35;
            }
            .donut__center {
                position: absolute;
                inset: 0;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                pointer-events: none;
                text-align: center;
            }
            .donut__center-value {
                font-size: 1.75rem;
                font-weight: 600;
                line-height: 1.1;
                color: var(--text-color);
            }
            .donut__center-unit {
                font-size: 0.875rem;
                margin-left: 0.125rem;
                color: var(--text-color-secondary);
            }
            .donut__center-label {
                font-size: 0.75rem;
                color: var(--text-color-secondary);
            }
            .legend {
                list-style: none;
                margin: 0;
                padding: 0;
                display: grid;
                grid-template-columns: auto 1fr auto auto;
                column-gap: 0.75rem;
                row-gap: 0.5rem;
                align-items: center;
                font-size: 0.875rem;
                flex: 1 1 14rem;
                max-width: 24rem;
            }
            .legend__row {
                display: grid;
                grid-column: 1 / -1;
                grid-template-columns: subgrid;
                align-items: center;
            }
            .legend__row > * {
                transition: opacity 120ms ease;
            }
            .legend__row--dim > * {
                opacity: 0.35;
            }
            .legend__swatch {
                width: 0.75rem;
                height: 0.75rem;
                border-radius: 3px;
                background: var(--c-light);
            }
            :host-context(.app-dark) .legend__swatch {
                background: var(--c-dark);
            }
            .legend__swatch--neutral,
            :host-context(.app-dark) .legend__swatch--neutral {
                background: var(--surface-border);
            }
            .legend__label {
                color: var(--text-color);
            }
            .legend__value,
            .legend__pct {
                text-align: right;
                white-space: nowrap;
                font-variant-numeric: tabular-nums;
                color: var(--text-color-secondary);
            }
            .donut-empty {
                display: flex;
                align-items: center;
                justify-content: center;
                min-height: 10rem;
                color: var(--text-color-secondary);
                font-size: 0.875rem;
            }
        `
    ]
})
export class MetricDonutComponent {
    readonly segments = input<DonutSegment[]>([]);
    /** Unit for segment values in the legend and tooltips (e.g. "h"). */
    readonly unit = input<string>('');
    readonly format = input<string>('1.0-1');
    /** Headline value in the hole (e.g. utilisation %, or an average rating), or null for none. */
    readonly centerValue = input<number | null>(null);
    readonly centerLabel = input<string>('');
    /** Unit printed after the headline value; a percentage by default. */
    readonly centerUnit = input<string>('%');
    readonly centerFormat = input<string>('1.0-0');
    readonly ariaLabel = input<string>('');
    readonly emptyText = input<string>('');

    readonly radius = RADIUS;
    readonly active = signal<string | null>(null);

    readonly total = computed(() => this.segments().reduce((sum, s) => sum + Math.max(0, s.value), 0));

    readonly arcs = computed(() => {
        const total = this.total();
        const segments = this.segments().filter((s) => s.value > 0);
        const gap = segments.length > 1 ? GAP : 0;
        let start = 0;
        return segments.map((s) => {
            const len = (s.value / total) * CIRCUMFERENCE;
            const visible = Math.max(0.5, len - gap);
            const arc = {
                ...s,
                pct: (s.value / total) * 100,
                light: s.slot === null ? null : METRIC_SERIES_LIGHT[s.slot % METRIC_SERIES_LIGHT.length],
                dark: s.slot === null ? null : METRIC_SERIES_DARK[s.slot % METRIC_SERIES_DARK.length],
                dash: `${visible} ${CIRCUMFERENCE - visible}`,
                offset: -(start + gap / 2)
            };
            start += len;
            return arc;
        });
    });
}
